import type { Dispatch, ReducersMapObject, Store } from "redux";
import { type TypedUseSelectorHook, useSelector, useDispatch } from "react-redux";
import type { CommonActionsType, CommonReducerType } from "../../../common-deprecated/redux/commonStore";
import modalReducer from "../../../common-deprecated/redux/reducers/ModalReducer";
import { getInitStoreByReducers } from "../../../common-deprecated/redux/storeHelpers";
import type { CreateLogicType } from "../../../common-deprecated/redux/typeHelpers";
import UscVehicleReducer, { type UscVehicleReducerType } from "./reducers/UscVehicleReducer";
import CommonSettingsReducer from "../../../common-deprecated/redux/reducers/CommonSettingsReducer";
import utilReducer from "../../../common-deprecated/redux/reducers/UtilReducer";
import type { UscVehicleActionsType } from "./actions/UscVehicleActions";
import UscBasketReducer, { type UscBasketReducerType } from "./reducers/UscBasketReducer";
import type { UscBasketActionsType } from "./actions/UscBasketActions";
import UscFinanceReducer, { type UscFinanceReducerType } from "./reducers/UscFinanceReducer";
import type { UscFinanceActionsType } from "./actions/UscFinanceActions";
import UscDealerReducer, { type UscDealerReducerType } from "./reducers/UscDealerReducer";
import type { UscDealerActionsType } from "./actions/UscDealerActions";
import UscSaveReducer, { type UscSaveReducerType } from "./reducers/UscSaveReducer";
import type { UscSaveActionsType } from "./actions/UscSaveActions";
import UscInsuranceReducer, { type UscInsuranceReducerType } from "./reducers/UscInsuranceReducer";
import type { UscInsuranceActionsType } from "./actions/UscInsuranceActions";
import UscExternalPriceReducer, { type UscExternalPriceReducerType } from "./reducers/UscExternalPriceReducer";
import type { UscExternalPriceActionsType } from "./actions/UscExternalPriceActions";
import basketLogic from "./logic/basketLogic";
import externalPriceLogic from "./logic/externalPriceLogic";
import financeLogic from "./logic/financeLogic";
import insuranceLogic from "./logic/insuranceLogic";
import modalLogic from "./logic/modalLogic";
import RetailerSettingsSlice, {
    type RetailerSettingsActionsType,
    type RetailerSettingsType,
} from "../../../shared-logic/features/retailer/redux/RetailerSettingsSlice";
import UscAccessoriesReducer, { type UscAccessoriesReducerType } from "./reducers/UscAccessoriesReducer";
import type { UscAccessoriesTypes } from "./actions/UscAccessoriesActions";
import UscCarDeliveryReducer, { type UscCarDeliveryReducerType } from "./reducers/UscCarDeliveryReducer";
import type { UscCarDeliveryActionsType } from "./actions/UscCarDeliveryActions";
import carDeliveryLogic from "./logic/carDeliveryLogic";
import notificationReducer from "../../../common-deprecated/redux/reducers/NotificationReducer";
import type { NotificationActionsType } from "../../../common-deprecated/redux/actions/NotificationActions";
import UscNavReducer, { type UscNavReducerType } from "./reducers/UscNavReducer";
import type { UscNavActionsType } from "./actions/UscNavActions";
import UscSettingsReducer, { type UscSettingsType } from "./reducers/UscSettingsReducer";
import type { UscSettingsActionsType } from "./actions/UscSettingsActions";
import historyLogic from "./logic/historyLogic";
import navigationLogic from "./logic/navigationLogic";
import type { EcoCostsActionsType } from "./actions/EcoCostsActions";
import ecoCostsReducer from "./reducers/EcoCostsReducers";
import type { EcoCostsReducerType } from "../utils/ecoCostsUtils";

export type UscStateType = CommonReducerType & {
    basket: UscBasketReducerType;
    finance: UscFinanceReducerType;
    insurance: UscInsuranceReducerType;
    vehicle: UscVehicleReducerType;
    accessories: UscAccessoriesReducerType;
    carDelivery: UscCarDeliveryReducerType;
    dealer: UscDealerReducerType;
    nav: UscNavReducerType;
    save: UscSaveReducerType;
    externalPrice: UscExternalPriceReducerType;
    uscSettings: UscSettingsType;
    [RetailerSettingsSlice.name]: RetailerSettingsType;
    ecoCosts: EcoCostsReducerType;
};

export type UscActionsType =
    | CommonActionsType
    | UscBasketActionsType
    | UscFinanceActionsType
    | UscInsuranceActionsType
    | UscVehicleActionsType
    | UscDealerActionsType
    | UscSaveActionsType
    | UscNavActionsType
    | UscSettingsActionsType
    | RetailerSettingsActionsType
    | UscExternalPriceActionsType
    | UscAccessoriesTypes
    | UscCarDeliveryActionsType
    | NotificationActionsType
    | EcoCostsActionsType;

export type UscDispatchType = Dispatch<UscActionsType>;

export type UscLogicType<ActionsType extends UscActionsType> = CreateLogicType<
    ActionsType,
    UscStateType,
    UscDispatchType
>;

export type UscStoreType = Store<UscStateType, UscActionsType>;

export const useUscDispatch = useDispatch<UscDispatchType>;

export const useUscSelector: TypedUseSelectorHook<UscStateType> = useSelector;

export const uscReducers: ReducersMapObject<UscStateType, any> = {
    basket: UscBasketReducer,
    finance: UscFinanceReducer,
    insurance: UscInsuranceReducer,
    externalPrice: UscExternalPriceReducer,
    vehicle: UscVehicleReducer,
    dealer: UscDealerReducer,
    accessories: UscAccessoriesReducer,
    carDelivery: UscCarDeliveryReducer,
    nav: UscNavReducer,
    save: UscSaveReducer,
    uscSettings: UscSettingsReducer,
    commonSettings: CommonSettingsReducer,
    [RetailerSettingsSlice.name]: RetailerSettingsSlice.reducer,
    modal: modalReducer,
    utils: utilReducer,
    notification: notificationReducer,
    ecoCosts: ecoCostsReducer,
};

export const uscServerStore = getInitStoreByReducers(uscReducers, []);
export const uscClientStore = getInitStoreByReducers(uscReducers, [
    ...historyLogic,
    ...basketLogic,
    ...externalPriceLogic,
    ...financeLogic,
    ...navigationLogic,
    ...insuranceLogic,
    ...modalLogic,
    ...carDeliveryLogic,
]);
